import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { QuoteIcon } from 'lucide-react';
import { openQuestion, themes } from '../../data/snakInsights';
const EASE = [0.23, 1, 0.32, 1] as const;
export function QualitativePanel() {
  const [active, setActive] = useState(0);
  const theme = themes[active];
  return <div className="grid gap-[clamp(0.6rem,1vw,0.9rem)] lg:grid-cols-12" data-id="element-82" data-mp="3v6oqhyl4fows">
      <motion.section initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.45,
      ease: EASE
    }} className="rounded-2xl bg-white p-[clamp(1rem,1.6vw,1.4rem)] ring-1 ring-[var(--fg)]/[0.07] lg:col-span-5" data-id="element-83" data-mp="309wcmka5eyf">
        <p className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-[var(--brand)]" data-id="element-84" data-mp="hqqv1y5j5qrm">Open question at the end of the Snak</p>
        <h3 className="mt-1.5 text-[clamp(0.95rem,1.3vw,1.2rem)] font-bold leading-snug text-[var(--fg)]" data-id="element-85" data-mp="mqvd3sm36tjy">“{openQuestion}”</h3>
        <p className="mt-1 text-[clamp(0.68rem,0.82vw,0.78rem)] text-[var(--muted)]" data-id="element-86" data-mp="qoszknoh31jn">96 answers · auto-coded into themes</p>

        <ul className="mt-4 space-y-1.5" role="tablist" aria-label="Themes" data-id="element-87" data-mp="2swnfzp4nvfyb">
          {themes.map((t, i) => <li key={t.theme} data-id="element-88" data-mp="2nwrfhzv6a6ni" data-mpi={i}>
              <button type="button" role="tab" aria-selected={active === i} onClick={() => setActive(i)} className={`w-full rounded-xl px-3 py-2.5 text-left transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--brand)] ${active === i ? 'bg-[var(--fg)] text-white' : 'hover:bg-[var(--surface)]'}`} data-id="element-89" data-mp="2nxt0aw1zq7if">
                <div className="flex items-baseline justify-between gap-3 text-[clamp(0.74rem,0.92vw,0.86rem)]" data-id="element-90" data-mp="31xlniheb65ln">
                  <span className="font-semibold" data-id="element-91" data-mp="37btrjat984q">{t.theme}</span>
                  <span className="font-bold tabular-nums" data-id="element-92" data-mp="3ulpxhf3wx4u5">{t.share}%</span>
                </div>
                <div className={`mt-1.5 h-1 overflow-hidden rounded-full ${active === i ? 'bg-white/15' : 'bg-[var(--fg)]/[0.07]'}`} data-id="element-93" data-mp="1so7a12g9h1qn">
                  <motion.div className={`h-full rounded-full ${active === i ? 'bg-[var(--brand-light)]' : 'bg-[var(--brand)]'}`} initial={{
                width: 0
              }} animate={{
                width: `${t.share / themes[0].share * 100}%`
              }} transition={{
                delay: 0.15 + i * 0.06,
                duration: 0.6,
                ease: EASE
              }} data-id="element-94" data-mp="1xnjzluyiomwx" />
                </div>
              </button>
            </li>)}
        </ul>
      </motion.section>

      <motion.section initial={{
      opacity: 0,
      y: 12
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      delay: 0.1,
      duration: 0.45,
      ease: EASE
    }} className="flex flex-col rounded-2xl bg-[var(--fg)] p-[clamp(1.1rem,1.8vw,1.6rem)] text-white lg:col-span-7" data-id="element-95" data-mp="1001my91qj0nn">
        <p className="font-mono text-[0.62rem] uppercase tracking-[0.14em] text-[var(--brand-lighter)]" data-id="element-96" data-mp="2zcdzxgum4kbr">Verbatims · {theme.theme}</p>
        <AnimatePresence mode="wait" data-id="element-97" data-mp="nq2a584d596j">
          <motion.ul key={theme.theme} initial={{
          opacity: 0,
          y: 8
        }} animate={{
          opacity: 1,
          y: 0
        }} exit={{
          opacity: 0,
          y: -6
        }} transition={{
          duration: 0.3,
          ease: EASE
        }} className="mt-4 flex-1 space-y-4" data-id="element-98" data-mp="nmxy5oknp95b">
            {theme.quotes.map(q => <li key={q.text} className="flex gap-3" data-id="element-99" data-mp="16f8uezkt28ug">
                <QuoteIcon className="mt-1 h-5 w-5 flex-shrink-0 text-[var(--brand-light)]" aria-hidden="true" data-id="element-100" data-mp="16hv2d0blyqd7" />
                <div data-id="element-101" data-mp="11j1rzgrmbfh9">
                  <p className="text-[clamp(1rem,1.6vw,1.45rem)] font-semibold leading-snug tracking-[-0.01em]" data-id="element-102" data-mp="1gehodsfo7ngt">{q.text}</p>
                  <p className="mt-1.5 font-mono text-[0.66rem] uppercase tracking-[0.12em] text-white/55" data-id="element-103" data-mp="1bfwndqslgzrb">{q.role}</p>
                </div>
              </li>)}
          </motion.ul>
        </AnimatePresence>
        <p className="mt-5 border-t border-white/10 pt-3 text-[0.66rem] leading-snug text-white/45" data-id="element-104" data-mp="3d7swyuv7bzjg">
          Fictional verbatims for illustration. In a live Snak, free text is anonymised and aggregated; potential adverse-event mentions are routed to
          pharmacovigilance.
        </p>
      </motion.section>
    </div>;
}